// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  User, Mail, Phone, MapPin, LogOut, Package, Heart,
  ShoppingBag, Edit, Save, X, Upload, Camera,
  ChevronRight, UserCircle, Truck, CheckCircle, Clock, XCircle
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const Profile = () => {
  const navigate = useNavigate();
  const { user, userData, loading: authLoading, logout, refreshUser } = useAuth();

  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '', phone: '', address: '', city: '',
    state: 'Punjab', postalCode: '',
  });

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Load user data
  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || '',
        phone: userData.phone || '',
        address: userData.address || '',
        city: userData.city || '',
        state: userData.state || 'Punjab',
        postalCode: userData.postalCode || '',
      });
    }
  }, [userData]);

  // Fetch orders
  useEffect(() => {
    if (activeTab === 'orders' && user?.email) {
      fetchOrders();
    }
  }, [activeTab, user?.email]);

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('customerEmail', '==', user.email));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setOrders(data);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
      });
      await refreshUser();
      setEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setShowSuccess(true);
    } catch (err) {
      console.error('Save error:', err);
      setMessage({ type: 'error', text: 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const url = await uploadToImageKit(file, 'customers');
      await updateDoc(doc(db, 'users', user.uid), { photoURL: url });
      await refreshUser();
      setSuccessMessage('Photo updated!');
      setShowSuccess(true);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Photo upload failed.' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || 'processing';
    if (s === 'completed' || s === 'delivered') return { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle };
    if (s === 'shipped') return { label: 'Shipped', color: 'bg-blue-100 text-blue-700', icon: Truck };
    if (s === 'cancelled') return { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle };
    if (s === 'out-for-delivery') return { label: 'Out for Delivery', color: 'bg-purple-100 text-purple-700', icon: Truck };
    return { label: 'Processing', color: 'bg-yellow-100 text-yellow-700', icon: Clock };
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  if (!user) return null;

  const tabs = [
    { key: 'overview', label: 'Overview', icon: UserCircle },
    { key: 'orders', label: 'My Orders', icon: Package },
    { key: 'addresses', label: 'Addresses', icon: MapPin },
    { key: 'wishlist', label: 'Wishlist', icon: Heart },
  ];

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">

        <h1 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: 'var(--color-primary-text)' }}>
          My Profile
        </h1>

        {message.text && (
          <div className={`text-sm p-3 rounded-lg mb-4 flex items-center gap-2 ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

          {/* SIDEBAR */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl p-5 shadow-md mb-4"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>

              <div className="text-center">
                <div className="relative inline-block">
                  <div className="w-24 h-24 rounded-full overflow-hidden mx-auto shadow-lg"
                    style={{ border: `3px solid var(--color-accent)` }}>
                    {userData?.photoURL ? (
                      <img src={userData.photoURL} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-3xl font-bold"
                        style={{ backgroundColor: 'var(--color-accent)' }}>
                        {(userData?.name || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer shadow-md btn-2d"
                    style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
                    {uploadingPhoto ? (
                      <span className="inline-block w-3 h-3 border-2 border-t-transparent rounded-full anim-spin"
                        style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />
                    ) : (
                      <Camera size={14} />
                    )}
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhoto} />
                  </label>
                </div>

                <h2 className="font-bold mt-3 text-lg" style={{ color: 'var(--color-primary-text)' }}>
                  {userData?.name || 'Customer'}
                </h2>
                <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>
                  {userData?.email || user.email}
                </p>
              </div>

              <nav className="mt-5 space-y-1">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.key;
                  return (
                    <button key={tab.key}
                      onClick={() => {
                        if (tab.key === 'wishlist') {
                          navigate('/profile/wishlist');
                          return;
                        }
                        setActiveTab(tab.key);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition text-left ${active ? 'text-white shadow-md' : ''}`}
                      style={{
                        backgroundColor: active ? 'var(--color-accent)' : 'transparent',
                        color: active ? '#fff' : 'var(--color-primary-text)',
                      }}>
                      <Icon size={18} />
                      <span className="font-medium text-sm">{tab.label}</span>
                      <ChevronRight size={14} className="ml-auto opacity-50" />
                    </button>
                  );
                })}

                <button onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg transition text-left text-red-600 hover:bg-red-50 mt-2">
                  <LogOut size={18} />
                  <span className="font-medium text-sm">Logout</span>
                </button>
              </nav>
            </div>
          </div>

          {/* CONTENT */}
          <div className="lg:col-span-3">

            {/* OVERVIEW */}
            {activeTab === 'overview' && (
              <div className="rounded-2xl p-6 shadow-md anim-fadeInUp"
                style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>

                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold" style={{ color: 'var(--color-primary-text)' }}>
                    Personal Information
                  </h2>
                  <button onClick={() => editing ? handleSave() : setEditing(true)}
                    disabled={saving}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm btn-2d ${saving ? 'opacity-70' : ''}`}
                    style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
                    {editing ? (
                      <><Save size={14} /> {saving ? 'Saving...' : 'Save'}</>
                    ) : (
                      <><Edit size={14} /> Edit</>
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-secondary-text)' }}>Full Name</label>
                    {editing ? (
                      <input type="text" name="name" value={formData.name} onChange={handleChange}
                        className="w-full rounded-lg py-2.5 px-4 text-sm focus:outline-none"
                        style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }} />
                    ) : (
                      <p className="py-2.5 font-semibold" style={{ color: 'var(--color-primary-text)' }}>{formData.name || 'Not set'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-secondary-text)' }}>Email</label>
                    <p className="py-2.5 font-semibold" style={{ color: 'var(--color-primary-text)' }}>{user.email}</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-secondary-text)' }}>Phone</label>
                    {editing ? (
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        className="w-full rounded-lg py-2.5 px-4 text-sm focus:outline-none"
                        style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }} />
                    ) : (
                      <p className="py-2.5 font-semibold" style={{ color: 'var(--color-primary-text)' }}>{formData.phone || 'Not set'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-secondary-text)' }}>City</label>
                    {editing ? (
                      <input type="text" name="city" value={formData.city} onChange={handleChange}
                        className="w-full rounded-lg py-2.5 px-4 text-sm focus:outline-none"
                        style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }} />
                    ) : (
                      <p className="py-2.5 font-semibold" style={{ color: 'var(--color-primary-text)' }}>{formData.city || 'Not set'}</p>
                    )}
                  </div>

                  {editing && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-secondary-text)' }}>State</label>
                        <select name="state" value={formData.state} onChange={handleChange}
                          className="w-full rounded-lg py-2.5 px-4 text-sm focus:outline-none"
                          style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}>
                          <option>Punjab</option>
                          <option>Sindh</option>
                          <option>Khyber Pakhtunkhwa</option>
                          <option>Balochistan</option>
                          <option>Islamabad</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-secondary-text)' }}>Postal Code</label>
                        <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange}
                          className="w-full rounded-lg py-2.5 px-4 text-sm focus:outline-none"
                          style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }} />
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--color-secondary-text)' }}>Full Address</label>
                  {editing ? (
                    <textarea name="address" value={formData.address} onChange={handleChange} rows={2}
                      className="w-full rounded-lg py-2.5 px-4 text-sm focus:outline-none resize-none"
                      style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }} />
                  ) : (
                    <p className="py-2.5 font-semibold" style={{ color: 'var(--color-primary-text)' }}>{formData.address || 'Not set'}</p>
                  )}
                </div>

                {editing && (
                  <button onClick={() => setEditing(false)}
                    className="mt-4 text-sm font-semibold flex items-center gap-1"
                    style={{ color: 'var(--color-secondary-text)' }}>
                    <X size={14} /> Cancel
                  </button>
                )}
              </div>
            )}

            {/* ORDERS */}
            {activeTab === 'orders' && (
              <div className="rounded-2xl p-6 shadow-md anim-fadeInUp"
                style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>

                <h2 className="text-lg font-bold mb-5" style={{ color: 'var(--color-primary-text)' }}>
                  My Orders ({orders.length})
                </h2>

                {ordersLoading ? (
                  <div className="text-center py-10">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-t-transparent"
                      style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-10">
                    <Package size={48} className="mx-auto mb-3" style={{ color: 'var(--color-secondary-text)' }} />
                    <p className="mb-4 text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                      Abhi tak koi order nahi kiya.
                    </p>
                    <Link to="/shop"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm btn-2d shadow-lg"
                      style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
                      <ShoppingBag size={14} /> Start Shopping
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.map(order => {
                      const statusConfig = getStatusConfig(order.status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <Link
                          key={order.id}
                          to={`/order-tracking/${order.id}`}
                          className="flex items-center gap-3 p-4 rounded-xl hover:shadow-md transition card-lift"
                          style={{ backgroundColor: 'var(--color-body-bg)', border: `1px solid var(--color-border)` }}
                        >
                          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white shrink-0"
                            style={{ backgroundColor: 'var(--color-accent)' }}>
                            <Package size={20} />
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate" style={{ color: 'var(--color-primary-text)' }}>
                              #{order.id.slice(-8).toUpperCase()}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--color-secondary-text)' }}>
                              {order.items?.length || 0} items • Rs. {order.total}
                            </p>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 ${statusConfig.color}`}>
                              <StatusIcon size={10} /> {statusConfig.label}
                            </span>
                          </div>

                          <div className="text-right shrink-0">
                            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-link)' }}>
                              Track →
                            </p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ADDRESSES */}
            {activeTab === 'addresses' && (
              <div className="rounded-2xl p-6 shadow-md anim-fadeInUp"
                style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
                <h2 className="text-lg font-bold mb-5" style={{ color: 'var(--color-primary-text)' }}>
                  Saved Address
                </h2>

                {formData.address ? (
                  <div className="p-4 rounded-xl"
                    style={{ backgroundColor: 'var(--color-body-bg)', border: `1px solid var(--color-border)` }}>
                    <p className="font-semibold mb-1" style={{ color: 'var(--color-primary-text)' }}>
                      {formData.name}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                      {formData.address}<br />
                      {formData.city}, {formData.state} {formData.postalCode}<br />
                      {formData.phone}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <MapPin size={48} className="mx-auto mb-3" style={{ color: 'var(--color-secondary-text)' }} />
                    <p className="text-sm mb-4" style={{ color: 'var(--color-secondary-text)' }}>
                      Koi address save nahi kiya.
                    </p>
                    <button
                      onClick={() => { setActiveTab('overview'); setEditing(true); }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm btn-2d shadow-lg"
                      style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
                      <Edit size={14} /> Add Address
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />
    </div>
  );
};

export default Profile;